import React from "react";
import {useTheme} from "styled-components";
import {useLocale, useMessages} from "i18n";
import {useUser} from "UserProvider";
import formatBytes from "core/format/byteFormat";
import {realName} from "nav/pro/useInlineName";
import {useMountedOrigin} from "utils/useMountedOrigin";
import {readableForegroundFor} from "../../account/brand/brandColors";
import {OwnerThemePoster} from "../../sponsor/takeover/OwnerThemePoster";
import {SponsorTakeoverArt} from "../../sponsor/takeover/SponsorTakeover";
import {isPageThemeTemplate} from "../../sponsor/sponsorBooking";
import {RollingCountdown} from "countdown/RollingCountdown";
import {DownArrowIcon, UpArrowIcon} from "./icons";
import {personalizationFontFamily} from "../personalization/personalizationFont";
import {
	PREVIEW_SAMPLE_FILE_BYTES, previewAddress, previewCompanySender, previewPageFor, previewSenderName,
	uploadPagePreview, type PersonalizationPreviewModel, type RequestPagePreviewInputs, type UploadPreviewAccount,
} from "../personalization/personalizationPreview";
import type {CompanyFields} from "sender/senderChoice";
import {
	PreviewArtLayer, PreviewBox, PreviewBrowserAddress, PreviewBrowserBar, PreviewBrowserDot, PreviewCaption,
	PreviewDevice, PreviewDownloadLabel, PreviewDownloadPill, PreviewDownloadRow,
	PreviewFileInfo, PreviewFileName, PreviewFileRow, PreviewFileSelect, PreviewFileSize,
	PreviewFileThumb, PreviewFilesHead, PreviewHeading, PreviewHolder, PreviewLive, PreviewLogoBadge,
	PreviewMessage, PreviewPosterRegion, PreviewRoot, PreviewScreen, PreviewSendPill, PreviewSenderName, PreviewSenderSub,
	PreviewStrip, PreviewStripCopy, PreviewStripSummary, PreviewStripThumb, PreviewStripToggle, PreviewUploadAvatar,
	PreviewUploadCountdown, PreviewUploadCountdownLabel, PreviewUploadCountdownValue, PreviewUploadLogoBadge,
	PreviewUploadSenderIdentity, PreviewUploadSenderName, PreviewUploadSenderRow,
} from "./PersonalisePreview.styles";

/**
 * The builder's live miniature of the page its reader opens (ui#1921): the recipient's download page, or, while the
 * panel asks for files, the upload page the request's contact opens (kitecorp/transferify-ui#3173). Both are the
 * recipient card on the look's art, so they share the device, the art and the sender's line on a wallpaper; only
 * the card differs.
 *
 * A DUMB view over {@link PersonalizationPreviewModel}: every value it draws — what the art is, which
 * pictures animate, whose logo and accent apply, how the message splits — is already decided in the
 * React-free `personalizationPreview.ts`. This file positions those values and nothing else, so the
 * "live" claim is testable where the mapping lives rather than only where it is painted.
 *
 * THE ART IS THE REAL RENDERER. `SponsorTakeoverArt` is the same component the recipient page mounts,
 * on `drive="sweep"` for the reason `TemplatePreviewLauncher` gives at its own mount: the page's hero is
 * scroll-linked and a preview has no scroll to link to, so the sweep feeds the identical transforms from
 * a self-playing clock. A preview-only renderer would be a second implementation of the same effects,
 * and a second implementation is what makes a preview drift from the page it previews. It also inherits
 * reduced motion for free — playback is decided once in `useTakeoverPlayback` and passed down — which a
 * renderer of our own is exactly the path that could quietly ignore.
 *
 * THE PAGE IS DRAWN INSIDE A DEVICE (ui#1952): a browser window with its bar and the recipient's address,
 * capped and centred in its column. Without the frame the miniature is a rectangle in a sheet full of
 * rectangles and reads as a strip once the column is wide; with it, it reads as the thing the recipient
 * will open, which is the approved prototype's picture.
 *
 * TWO SESSION FACTS ARE READ HERE rather than handed in with the model: whose account this is (its name and
 * address, and for a request its company), and which origin the browser is on. Neither is a fact about the
 * DRAFT — `personalizationPreview` maps the draft and stays a pure function of it — and both are exactly the
 * things the reader's page shows that the sender never types: the name the api discloses when no name was given,
 * and the site the link opens on. The rules that turn them into what is drawn (`previewSenderName`,
 * `uploadPagePreview`, `previewAddress`) stay React-free in the model; this file only supplies their inputs from
 * the hooks that already own them.
 *
 * NOTHING HERE IS OPERABLE, and the device is `aria-hidden`. It is a picture of a page the sender is
 * composing, not a copy of it: every control it shows is the recipient's, and walking a screen-reader
 * user through a set of dead radio-shaped circles and a fake download button would be noise on top of
 * the real controls beside it. What a screen-reader user gets instead is the one-line summary, which is
 * the same line the phone dock shows and is announced as it changes.
 */

/**
 * The device the page is drawn in — the outermost thing the sender sees, so also what a spec measures
 * for the cap and the centring, and where `aria-hidden` sits.
 */
export const PERSONALISE_PREVIEW_DEVICE_TESTID = "personalise-preview-device";

/** The device's browser bar, so a spec can prove the frame is there without matching its address. */
export const PERSONALISE_PREVIEW_BAR_TESTID = "personalise-preview-bar";

/** The page inside the device — the surface the payload box's share is measured against. */
export const PERSONALISE_PREVIEW_TESTID = "personalise-preview";

/** The miniature's own art mount. Its OWN id: the default one is what specs prove ABSENCE with. */
export const PERSONALISE_PREVIEW_ART_TESTID = "personalise-preview-art";

/**
 * The sender's line on the miniature's wallpaper. Its own id for the reason the art has one: the
 * recipient page's poster carries the default, and a spec has to be able to say which page it read.
 */
export const PERSONALISE_PREVIEW_POSTER_TESTID = "personalise-preview-poster";

/** The phone dock's summary line, which is also the miniature's accessible description. */
export const PERSONALISE_PREVIEW_SUMMARY_TESTID = "personalise-preview-summary";

/**
 * The caption row: the label and the live badge, which share one line.
 *
 * Addressed by id because what a spec asserts about it is GEOMETRY — how many line boxes its two texts
 * occupy (ui#1942, where they occupied three) — and a locator built from the copy would have to be
 * rewritten every time either string is reworded.
 */
export const PERSONALISE_PREVIEW_CAPTION_TESTID = "personalise-preview-caption";

/**
 * The payload box, and the sender's name inside it.
 *
 * Ids rather than text locators because the strings here are the SENDER's own words: a spec that found
 * them by their text would be matching data it typed a moment earlier, which `scripts/e2e-copy-sweep.sh`
 * refuses for the same reason it refuses retyped copy — the locator stops describing the element.
 */
export const PERSONALISE_PREVIEW_BOX_TESTID = "personalise-preview-box";
export const PERSONALISE_PREVIEW_NAME_TESTID = "personalise-preview-name";
/** The company's tax ID, on the line under the sender's name as the page shows it (owner 2026-10-02). */
export const PERSONALISE_PREVIEW_TAX_ID_TESTID = "personalise-preview-tax-id";

/** The sender's mark on the miniature — how a spec proves a logo reached the page, and left it again. */
export const PERSONALISE_PREVIEW_LOGO_TESTID = "personalise-preview-logo";

/**
 * The download pill's caption.
 *
 * Its OWN id because the two captions it alternates between are a prefix pair — "Download" is a
 * substring of "Download all" — so a spec reading the miniature's whole text cannot tell them apart.
 */
export const PERSONALISE_PREVIEW_DOWNLOAD_TESTID = "personalise-preview-download";

/** The upload page's Send files button, and the request's note above it (kitecorp/transferify-ui#3173). */
export const PERSONALISE_PREVIEW_SEND_TESTID = "personalise-preview-send";
export const PERSONALISE_PREVIEW_NOTE_TESTID = "personalise-preview-note";

export interface PersonalisePreviewProps {
	preview: PersonalizationPreviewModel;
	/**
	 * The company the transfer being composed goes out as, which its download page names, or null for one a person
	 * sends (kitecorp/transferify-ui#3186). A request's upload page names its sender from `request` instead.
	 */
	company?: CompanyFields | null;
	/**
	 * The request being composed while the panel asks for files: the miniature then draws the upload page its
	 * contact opens (kitecorp/transferify-ui#3173). Null draws the download page a transfer's recipient opens.
	 */
	request?: RequestPagePreviewInputs | null;
}

export function PersonalisePreview({preview, company = null, request = null}: PersonalisePreviewProps): React.ReactElement {
	const {sender, account, download, reminders} = useMessages();
	const copy = sender.personalise;
	const {locale} = useLocale();
	const theme = useTheme();
	const [user] = useUser();
	const currentOrigin = useMountedOrigin();
	const [expanded, setExpanded] = React.useState(false);
	/*
	 * The box the wallpaper's picture fills, handed to the poster so its contrast scrim can sample the
	 * pixels under the sender's line — the same arrangement the recipient page makes with its own art
	 * layer. State rather than a ref, because the sampling effect has to re-run when the element arrives;
	 * a ref's `current` changing schedules nothing.
	 */
	const [artLayer, setArtLayer] = React.useState<HTMLDivElement | null>(null);

	// The account as the recipient page would be told about it. `realName` is the nav's own reading of
	// a nameless account — blank, or the legacy "null null" a stale session can still carry — so the
	// miniature and the profile menu cannot disagree about whether this sender has a name.
	// A transfer sent as a company is named as its download page will name it (kitecorp/transferify-ui#3186).
	const senderAccount = {name: realName(user.fullname), email: user.email};
	const {name: senderName, taxId: senderTaxId} = company === null
		? {name: previewSenderName(preview.senderName, senderAccount), taxId: null}
		: previewCompanySender(senderAccount, company, reminders.options.companyIdLine);
	const page = previewPageFor(request !== null);
	const address = previewAddress(currentOrigin, page);

	/*
	 * The accent, and the foreground it will be read against.
	 *
	 * `readableForegroundFor` is the client mirror of the derivation the api performs, and using it HERE
	 * is not the client re-deriving contrast: nothing computed on this line is ever sent or saved — the
	 * transfer still carries the hex alone and `onAccentColor` still comes back from the api. It is the
	 * same use `BrandColorPalette` already makes of it for the chosen swatch's ring, and the alternative
	 * is a preview that paints its button's label in a colour the real page will not use.
	 */
	const accent = preview.accentColor ?? theme.tokens.blue;
	const inkOnAccent = preview.accentColor === null
		? theme.tokens.blueInk
		: readableForegroundFor(preview.accentColor);

	const size = formatBytes(PREVIEW_SAMPLE_FILE_BYTES, locale);
	const lookName = preview.template !== null && isPageThemeTemplate(preview.template)
		? account.theme.templates[preview.template].name
		: copy.templateNone;
	const faceName = preview.font === null ? copy.fontStandard : personalizationFontFamily(preview.font);
	const colorName = preview.colorId === null
		? (preview.accentColor ?? copy.colorLabel)
		: account.brand.palette.colors[preview.colorId];
	// The same separator the font tiles already use one section over, so the sheet has one list idiom.
	const summary = `${lookName} · ${faceName} · ${colorName}`;
	const thumbnailUrl = preview.art?.imageUrls[0] ?? null;
	// The look's accent as the upload page's card receives it (`recipientBrand.brandStyle`), so its badge and its
	// countdown's tiles wear it as there; without one they fall back to the page's own colours.
	const pageBrand = preview.accentColor === null ? {} : {
		"--recipient-brand": preview.accentColor,
		"--recipient-brand-on": inkOnAccent,
	};

	const miniature = (
		<PreviewDevice data-testid={PERSONALISE_PREVIEW_DEVICE_TESTID} aria-hidden="true">
			{/* Three window dots and the address. The dots are drawn, not copy; the address is a URL
			    built from the origin the sender is on, so neither is a string the catalogue owns. */}
			<PreviewBrowserBar data-testid={PERSONALISE_PREVIEW_BAR_TESTID}>
				<PreviewBrowserDot/>
				<PreviewBrowserDot/>
				<PreviewBrowserDot/>
				<PreviewBrowserAddress>{address}</PreviewBrowserAddress>
			</PreviewBrowserBar>
			<PreviewScreen
				data-testid={PERSONALISE_PREVIEW_TESTID}
				data-preview-page={page}
				style={{
					"--preview-accent": accent,
					"--preview-ink-on-accent": inkOnAccent,
					"--preview-font": preview.fontStack,
				} as React.CSSProperties}
			>
				<PreviewArtLayer ref={setArtLayer} $stage={preview.stage}>
					{preview.art !== null && (
						<SponsorTakeoverArt
							art={preview.art}
							testId={PERSONALISE_PREVIEW_ART_TESTID}
							drive="sweep"
							stage="tile"
						/>
					)}
				</PreviewArtLayer>
				{/*
				  * THE SENDER'S LINE, through the recipient page's own poster (owner, 2026-09-03). The same
				  * component `DownloadPage` mounts for a wallpaper theme, composed into the region beside
				  * the box, so the placement, the tone, the colour and the contrast scrim are the page's
				  * rules rather than a second set. WHETHER there is a line is the model's answer
				  * (`posterLine`, via `wallpaperLine`): a backdrop has none, and a blank is nothing.
				  */}
				{preview.posterLine !== null && preview.art !== null && (
					<PreviewPosterRegion>
						<OwnerThemePoster
							line={preview.posterLine}
							options={preview.art.options}
							imageUrls={preview.art.imageUrls}
							artLayer={artLayer}
							composed
							testId={PERSONALISE_PREVIEW_POSTER_TESTID}
						/>
					</PreviewPosterRegion>
				)}
				{request !== null ? (
					<UploadPageCard
						preview={preview}
						account={{name: senderAccount.name, company: user.company}}
						request={request}
						size={size}
						style={pageBrand as React.CSSProperties}
					/>
				) : (
				<PreviewBox data-testid={PERSONALISE_PREVIEW_BOX_TESTID}>
					{preview.logoUrl !== null && (
						<PreviewLogoBadge data-testid={PERSONALISE_PREVIEW_LOGO_TESTID}>
							{/* Decorative twice over: the miniature is aria-hidden, and the sender is looking at
							    their own upload with the logo row's own controls beside it. */}
							<img src={preview.logoUrl} alt=""/>
						</PreviewLogoBadge>
					)}
					{senderName !== null && (
						<PreviewSenderName data-testid={PERSONALISE_PREVIEW_NAME_TESTID}>{senderName}</PreviewSenderName>
					)}
					{senderTaxId !== null && (
						<PreviewSenderSub data-testid={PERSONALISE_PREVIEW_TAX_ID_TESTID}>{senderTaxId}</PreviewSenderSub>
					)}
					{preview.heading !== null && <PreviewHeading>{preview.heading}</PreviewHeading>}
					{preview.body !== "" && <PreviewMessage>{preview.body}</PreviewMessage>}
					{/* The recipient page's own words for its files head, so the two never quote it differently.
					    The COUNT is the transfer's real one and the size is the stand-in: one row stands for
					    the set (owner, 2026-09-03), and a head reading "1 file" over a "Download all" button
					    would be the miniature contradicting itself. */}
					<PreviewFilesHead>{download.file.summary(preview.fileCount, size)}</PreviewFilesHead>
					<PreviewFileRow>
						<PreviewFileThumb/>
						<PreviewFileInfo>
							<PreviewFileName>{copy.previewFileName}</PreviewFileName>
							<PreviewFileSize>{size}</PreviewFileSize>
						</PreviewFileInfo>
						<PreviewFileSelect/>
					</PreviewFileRow>
					<PreviewDownloadRow>
						<PreviewDownloadPill>
							<DownArrowIcon size={11}/>
							{/* WHICH caption is the model's answer, the wording is the catalogue's — the
							    same split `download/redownload` makes on the real page. */}
							<PreviewDownloadLabel data-testid={PERSONALISE_PREVIEW_DOWNLOAD_TESTID}>
								{download.all[preview.downloadLabelKey]}
							</PreviewDownloadLabel>
						</PreviewDownloadPill>
					</PreviewDownloadRow>
				</PreviewBox>
				)}
			</PreviewScreen>
		</PreviewDevice>
	);

	return (
		<PreviewRoot>
			{/*
			  * The header, and on a phone the DOCK: the thumbnail and the disclosure appear only there,
			  * where the miniature itself is folded away until it is asked for. One header for both, so
			  * the summary below is written once and announced once.
			  */}
			<PreviewStrip>
				<PreviewStripThumb
					aria-hidden="true"
					style={{"--preview-accent": accent} as React.CSSProperties}
				>
					{thumbnailUrl !== null && <img src={thumbnailUrl} alt=""/>}
				</PreviewStripThumb>
				<PreviewStripCopy>
					<PreviewCaption data-testid={PERSONALISE_PREVIEW_CAPTION_TESTID}>
						<span>{copy.previewLabel}</span>
						<PreviewLive>{copy.previewLive}</PreviewLive>
					</PreviewCaption>
					{/*
					  * The one line a screen-reader user gets instead of the picture, and the reason the
					  * miniature can be `aria-hidden` without hiding what it says. `role="status"` so a
					  * change is announced without stealing focus from the field being typed into.
					  */}
					<PreviewStripSummary data-testid={PERSONALISE_PREVIEW_SUMMARY_TESTID} role="status">
						{summary}
					</PreviewStripSummary>
				</PreviewStripCopy>
				<PreviewStripToggle
					aria-expanded={expanded}
					aria-label={expanded ? copy.previewCollapse : copy.previewExpand}
					onClick={() => setExpanded(open => !open)}
				>
					{expanded ? <UpArrowIcon size={16}/> : <DownArrowIcon size={16}/>}
				</PreviewStripToggle>
			</PreviewStrip>
			<PreviewHolder $expanded={expanded}>{miniature}</PreviewHolder>
		</PreviewRoot>
	);
}

/**
 * The upload page's card (kitecorp/transferify-ui#3173), in the order `RemindersUploadPage` lays it out: the look's
 * logo on its accent straddling the top edge, the sender row, the page's own heading, the look's message, the
 * request's note, the countdown to its close, and the selection a contact sends with Send files. One file row stands
 * for the set, as the download card's does. What each part says is `uploadPagePreview`'s; this only places it.
 *
 * THE COUNTDOWN IS THE PAGE'S OWN, and it starts from the whole period when the card is drawn, which is when the
 * builder opens: that is what the page reads when a link request is made or its email goes out.
 */
function UploadPageCard({preview, account, request, size, style}: {
	preview: PersonalizationPreviewModel;
	account: UploadPreviewAccount;
	request: RequestPagePreviewInputs;
	size: string;
	style: React.CSSProperties;
}) {
	const {sender, reminders} = useMessages();
	// Held, so the countdown ticks on from the first drawing rather than restarting on every keystroke.
	const [drawnAt] = React.useState(() => Date.now());
	const card = uploadPagePreview(preview, account, request, reminders.options.companyIdLine, drawnAt);
	return (
		<PreviewBox data-testid={PERSONALISE_PREVIEW_BOX_TESTID} style={style}>
			{preview.logoUrl !== null && (
				<PreviewUploadLogoBadge data-testid={PERSONALISE_PREVIEW_LOGO_TESTID}>
					<img src={preview.logoUrl} alt=""/>
				</PreviewUploadLogoBadge>
			)}
			<PreviewUploadSenderRow>
				<PreviewUploadAvatar>{card.sender.initial}</PreviewUploadAvatar>
				{/* The name, and a company's tax ID on the quieter line under it, as the page's sender row stacks them. */}
				<PreviewUploadSenderIdentity>
					{card.sender.display !== null && (
						<PreviewUploadSenderName data-testid={PERSONALISE_PREVIEW_NAME_TESTID}>
							{card.sender.display}
						</PreviewUploadSenderName>
					)}
					{card.senderTaxId !== null && (
						<PreviewSenderSub data-testid={PERSONALISE_PREVIEW_TAX_ID_TESTID}>{card.senderTaxId}</PreviewSenderSub>
					)}
				</PreviewUploadSenderIdentity>
			</PreviewUploadSenderRow>
			<PreviewHeading>{reminders.upload.heading}</PreviewHeading>
			{card.lookMessage !== "" && <PreviewMessage>{card.lookMessage}</PreviewMessage>}
			{card.note !== null && <PreviewMessage data-testid={PERSONALISE_PREVIEW_NOTE_TESTID}>{card.note}</PreviewMessage>}
			{card.closesAtMs !== null && (
				<PreviewUploadCountdown>
					<PreviewUploadCountdownLabel>{reminders.upload.timeLeft}</PreviewUploadCountdownLabel>
					<PreviewUploadCountdownValue>
						<RollingCountdown expiresAtMs={card.closesAtMs} endedLabel={reminders.item.closing.ended}/>
					</PreviewUploadCountdownValue>
				</PreviewUploadCountdown>
			)}
			<PreviewFilesHead>{reminders.upload.uploadFilesCount(1)}</PreviewFilesHead>
			<PreviewFileRow>
				<PreviewFileThumb/>
				<PreviewFileInfo>
					<PreviewFileName>{sender.personalise.previewFileName}</PreviewFileName>
					<PreviewFileSize>{size}</PreviewFileSize>
				</PreviewFileInfo>
			</PreviewFileRow>
			<PreviewDownloadRow>
				<PreviewSendPill>
					<UpArrowIcon size={11}/>
					<PreviewDownloadLabel data-testid={PERSONALISE_PREVIEW_SEND_TESTID}>
						{reminders.upload.sendFiles}
					</PreviewDownloadLabel>
				</PreviewSendPill>
			</PreviewDownloadRow>
		</PreviewBox>
	);
}
